import {Component, ElementRef, OnInit, ViewChild, viewChild} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/autocomplete';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSelect } from '@angular/material/select';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SharedModule } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { TestDataComponent } from '../../../test-data/test-data.component';
import { TranslateModule } from '@ngx-translate/core';
import { ToolDetailItem } from '../../../../model/tool-detail.model';
import { BookToolDto, BulkBookItemDto, ToolBookingDto, ToolBookingValidateItemDto } from '../../../../model/booking.models';
import { DropdownItemDto } from '../../../../model/dropdown-item.model';
import { ToolLocationDetail } from '../../../../model/tool-location-detail.model';
import { ToolService } from '../../../../service/tool.service';
import { InventoryService } from '../../../../service/inventory.service';
import { ToolLocationService } from '../../../../service/tool-location.service';
import { TransactionService } from '../../../../service/transaction.service';
import { CantaaMessageService } from '../../../../service/cantaa-message.service';
import { ToolLocationDocumentService } from '../../../../service/tool-location-document.service';
import { ToolDocumentService } from '../../../../service/tool-document.service';
import { DocumentService } from '../../../../service/document.service';
import { ToolConstants } from '../../../../constants/tool-constants';
import { UuidHelperService } from '../../../../service/helper/uuid-helper.service';
import { PTableHelper } from '../../../../service/helper/p-table-helper';
import { LocationType } from '../../../../constants/location-type';
import { FocusHelper } from '../../../../service/helper/focus-helper';
import { SuggestionInputComponent } from '../../../inputs/suggestion-input/suggestion-input.component';
import { ToolLocationListItem } from '../../../../model/tool-location-list-item.model';
import { ToolListItem } from '../../../../model/tool-list-item.model';
import { DisplayHelper } from '../../../../service/helper/DisplayHelper';
import {ProjectListItem} from "../../../../model/project-list.item";
import {ProjectDetailItem} from "../../../../model/project-detail.item";
import {ProjectService} from "../../../../service/project.service";
import {WimTreeNode} from "../../../../model/wim-tree.node";
import {CantaaLocalizationService} from "../../../../service/cantaa-localization.service";

@Component({
  selector: 'wim-bulk-booking-return',
  standalone: true,
  imports: [
    CommonModule,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
    MatHint,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatOption,
    MatRadioButton,
    MatRadioGroup,
    MatSelect,
    MatSuffix,
    ReactiveFormsModule,
    SharedModule,
    TableModule,
    TestDataComponent,
    TranslateModule,
    FormsModule,
    SuggestionInputComponent
  ],
  templateUrl: './bulk-booking-return.component.html',
  styleUrl: './bulk-booking-return.component.scss'
})
export class BulkBookingReturnComponent implements OnInit {
  public readonly RETURN = 'Return';
  protected readonly DisplayHelper = DisplayHelper;
  private inventoryLoc: string = this.localization.instant('AMOUNT.AMOUNT');

  dataTable = viewChild.required<Table>('dt');
  fromLocationSuggestionInput = viewChild.required<SuggestionInputComponent<ToolLocationListItem>>('fromLocationSuggestionInput');
  toolSuggestionInput = viewChild.required<SuggestionInputComponent<ToolListItem>>('toolSuggestionInput');
  fromProjectSuggestionInput = viewChild.required<SuggestionInputComponent<ProjectListItem>>('fromProjectSuggestionInput');

  bookItems: BulkBookItemDto[] = [];

  myLocationForm = this.fb.group({
    myLocation: this.fb.control<DropdownItemDto | null>(null)
  });

  amountForm = this.fb.group({
    toolAmount: [{ value: 1, disabled: true }, [Validators.required, Validators.min(0)]]
  });

  selectedTool: ToolDetailItem | null = null;
  toolLocations: DropdownItemDto[] = [];
  selectedFromLocation: ToolLocationDetail | null = null;
  selectedFromProject: ProjectDetailItem | null = null;

  constructor(private fb: FormBuilder,
              private toolService: ToolService,
              private inventoryService: InventoryService,
              private toolLocationService: ToolLocationService,
              private transactionService: TransactionService,
              private messageService: CantaaMessageService,
              private el: ElementRef,
              private toolLocationDocumentService: ToolLocationDocumentService,
              protected toolDocumentService: ToolDocumentService,
              private documentService: DocumentService,
              private toolInventoryService: InventoryService,
              private projectService: ProjectService,
              private localization: CantaaLocalizationService) {
  }

  async ngOnInit() {
    this.toolLocations = await this.toolLocationService.getToolLocationsForBooking();
    const preselected = await this.preselectDefaultLocation();

    if (!preselected) {
      this.focusToMyLocation();
    }
  }

  private async preselectDefaultLocation() {
    let defaultLocationId = await this.toolLocationService.getDefaultLocationId();

    if (defaultLocationId == null) {
      return false;
    }
    let myLocation = this.toolLocations.find(i => i.id === defaultLocationId);
    if (myLocation) {
      this.myLocationForm.controls.myLocation.setValue(myLocation);
      this.onMyLocationChange();
      return true;
    }
    return false;
  }

  onMyLocationChange() {
    const myLocationId = this.myLocationForm.value.myLocation?.id;
    if (myLocationId) {
      this.toolLocationDocumentService.getDefaultPhotoScaled70x70_2ImgEleId(myLocationId, 'return_myLocationForm_icon');
    }
    this.fromLocationSuggestionInput().clear();
    this.selectedFromLocation = null;
    this.fromLocationSuggestionInput().enable()
    this.fromLocationSuggestionInput().focus();
  }

  @ViewChild('fromLocationSuggestionInput') fromLocationComponent!: SuggestionInputComponent<ToolLocationListItem>;

  async onFromLocationSelection(toolLocationListItem: ToolLocationListItem | null) {
    if (!toolLocationListItem) {
      this.toolSuggestionInput().enable();
      this.toolSuggestionInput().focus();

      this.myLocationForm.controls.myLocation.disable();
      this.fromLocationSuggestionInput().disable();
      return;
    }

    this.selectedFromLocation = await this.toolLocationService.getToolLocationDetailById(toolLocationListItem.id);
    this.messageService.success('LOCATION.FOUND');

    if (this.selectedFromLocation.id !== null) {
      this.toolLocationDocumentService.getDefaultPhotoScaled70x70_2ImgEleId(this.selectedFromLocation.id, 'fromToLocationForm_icon');
    }

    this.toolSuggestionInput().enable();
    this.toolSuggestionInput().focus();

    this.myLocationForm.controls.myLocation.disable();
    this.fromLocationSuggestionInput().disable();
  }

  async onToolSelection(toolListItem: ToolListItem | null) {

    if (toolListItem == null) {
      return;
    }

    this.selectedTool = await this.toolService.getToolDetail(toolListItem.id);
    this.messageService.success('TOOL.FOUND');

    const amount = await this.getToolAmount();
    this.amountForm.controls.toolAmount.setValue(amount);

    this.toolDocumentService.getDefaultPhotoScaled70x70_2ImgEleId(this.selectedTool.id, 'tool_icon');
    this.fromProjectSuggestionInput().enable();
    this.fromProjectSuggestionInput().triggerSearch();
    this.fromProjectSuggestionInput().focus();
  }

  private async getToolAmount() {

    const toolLocation = this.selectedFromLocation!;
    const locationTypeCode = toolLocation?.locationTypeCode;

    if (LocationType.CONTAINER != locationTypeCode && LocationType.EMPLOYEE != locationTypeCode) {
      return null;
    }

    const amount = await this.inventoryService.getInventoryAmountByLocation(toolLocation?.id!,
      this.selectedTool?.id!, this.selectedFromProject?.id ?? null);

    return amount ?? null;
  }

  async onAmountSubmit() {
    await this.processItem();
  }

  private async processItem() {

    const fromLocationId = this.selectedFromLocation?.id;
    const toLocationId = this.myLocationForm.value.myLocation?.id;
    const tool = this.selectedTool;
    var amount = this.amountForm.controls.toolAmount.value;
    const fromProjectId = this.selectedFromProject?.id;

    if (!tool) {
      this.messageService.error('Kein Tool gewählt!');
      return;
    }

    if (!toLocationId) {
      this.messageService.error('Kein Ziel gewählt!');
      return;
    }

    if (!amount || amount === 0) {
      amount = 1;
    }

    const bookItem = await this.processItemDetail(tool, fromLocationId, toLocationId, amount, fromProjectId);

    PTableHelper.navigateToBottomOfTable(this.dataTable(), this.bookItems.length);
    this.setUiAfterProcessItem(bookItem);
    this.toolSuggestionInput().focus();
  }

  private async processItemDetail(tool: ToolDetailItem,
                                  fromLocationId: number | null | undefined, toLocationId: number,
                                  amount: number, fromProjectId: number | null | undefined): Promise<BulkBookItemDto> {

    const itemToValidate = {
      fromLocationId: fromLocationId,
      toLocationId: toLocationId,
      toolId: tool.id,
      amount: amount,
      fromProjectId: fromProjectId
    } as ToolBookingValidateItemDto;

    let validationMessages = await this.transactionService.validateToolBooking(itemToValidate, this.RETURN);

    const bookItem = {
      id: UuidHelperService.generateUuid(),
      tool: tool,
      amount: amount,
      fromLocationId: fromLocationId,
      toLocationId: toLocationId,
      message: '',
      messages: validationMessages,
      active: true,
      fromProjectId: fromProjectId
    } as BulkBookItemDto;

    this.bookItems = this.bookItems.concat(bookItem);
    return bookItem;
  }

  private setUiAfterProcessItem(bookItem: BulkBookItemDto) {
    this.selectedTool = null;
    this.toolSuggestionInput().clear();
    this.toolSuggestionInput().enable();

    this.amountForm.controls.toolAmount.setValue(1);
    this.amountForm.controls.toolAmount.disable();

    setTimeout(() => {
      this.toolDocumentService.getDefaultPhotoScaled70x70AndRender(bookItem.tool.id);
    }, 0);
    this.documentService.disableImgElement('tool_icon');
  }

  getActiveItems() {
    return this.bookItems.filter(i => i.active);
  }

  async onBulkBooking() {
    let tools = this.getActiveItems()
      .map(i => ({
        toolId: i.tool.id,
        amount: i.amount,
        fromProjectId: i.fromProjectId
      } as ToolBookingDto));

    const booking = {
      transactionTypeCode: this.RETURN,
      fromLocationId: this.getActiveItems()[0].fromLocationId,
      toLocationId: this.getActiveItems()[0].toLocationId,
      tools: tools
    } as BookToolDto

    await this.transactionService.makeBooking(booking)

    this.messageService.success('BOOKING.SUCCESSFUL');
    this.setUiAfterBook();
  }

  private setUiAfterBook() {
    this.bookItems = [];

    this.myLocationForm.controls.myLocation.enable();

    this.selectedFromLocation = null;
    this.fromLocationSuggestionInput().clear();
    this.fromLocationSuggestionInput().enable()
    this.fromLocationSuggestionInput().focus();
    this.toolSuggestionInput().disable();

    this.selectedTool = null;
    this.toolSuggestionInput().clear();
    this.toolSuggestionInput().disable()

    this.selectedFromProject = null;
    this.fromProjectSuggestionInput().clear();
    this.fromProjectSuggestionInput().disable()

    this.amountForm.controls.toolAmount.setValue(1);
    this.amountForm.controls.toolAmount.disable();
  }

  focusToMyLocation() {
    FocusHelper.focusFormField(this.el, 'myLocation');
  }

  focusToAmountInput() {
    FocusHelper.focusFormField(this.el, 'toolAmount');
  }

  onUpdateActiveState(item: BulkBookItemDto) {
    item.active = !item.active;
  }

  getTdClass(item: BulkBookItemDto) {
    return item.active ? "" : 'strikethrough';
  }

  onCancel() {
    this.setUiAfterBook();
  }

  fromLocationFetchSuggestions = async (query: string): Promise<ToolLocationListItem[]> => {
    return await this.toolLocationService.searchLocations(query);
  };

  toolFetchSuggestions = async (query: string): Promise<ToolListItem[]> => {
    return await this.toolService.searchTools(query);
  };

  async fillAllToolsInList() {

    const fromLocationId = this.selectedFromLocation?.id;
    const fromLocationPretty = this.selectedFromLocation?.locationPretty;
    const toLocationId = this.myLocationForm.value.myLocation?.id;
    const fromProjectId = this.selectedFromProject?.id;

    if (!toLocationId) {
      this.messageService.error('TARGET.NOT_CHOOSEN');
      return;
    }

    if (!fromLocationId) {
      this.messageService.error('ORIGIN.NOT_CHOOSEN');
      this.fromLocationSuggestionInput().focus();
      return;
    }

    const inventory = await this.toolInventoryService.getToolLocationInventory(fromLocationId);

    var negativeCount: number = 0;
    for (const item of inventory) {

      if (item.amount > 0) {
        const tool = await this.toolService.getToolDetail(item.toolId);
        await this.processItemDetail(tool, fromLocationId, toLocationId, item.amount, fromProjectId);
        this.toolDocumentService.getDefaultPhotoScaled70x70AndRender(item.toolId);
        this.documentService.disableImgElement('tool_icon');
      } else {
        negativeCount++;
      }
    }
    if (negativeCount > 0) {
      this.messageService.warn('INVENTORY.NEGATIVE_NOT_BOOKED', 'INVENTORY.NEGATIVE_COUNT', {negativeCount: negativeCount});
    }

    this.messageService.success('INVENTORY.FOUND_COUNT', {count: inventory.length, from: fromLocationPretty});
  }

  private async selectProject(projectId: number) {

    if (projectId == null) {
      return;
    }

    const project = await this.projectService.getProjectDetail(projectId);

    if (project == null) {
      return;
    }

    this.selectedFromProject = project;
    const projectListItem = this.mapToProjectListItem(this.selectedFromProject);
    this.fromProjectSuggestionInput().setValue(projectListItem);
  }

  private mapToProjectListItem(project: ProjectDetailItem): ProjectListItem {
    return {
      id: project.id!,
      treeNodeLabel: '',
      treeNode: null as any,
      rfid: '',
      projectType: project.projectType,
      number: project.number,
      status: project.status,
      startDate: project.startDate ?? '',
      terminationDate: project.terminationDate ?? '',
      amount: null
    };
  }

  fromProjectFetchSuggestions = async (query: string): Promise<ProjectListItem[]> => {

    console.log('fromProjectFetchSuggestions:', query);

    if (!this.selectedTool) {
      return [];
    }

    const emptyProject: ProjectListItem = {
      id: -1,
      treeNodeLabel: '',
      treeNode: {} as WimTreeNode,
      rfid: '',
      projectType: '',
      number: '',
      status: '',
      startDate: '',
      terminationDate: '',
      amount: null
    };

    const projects = await this.projectService.searchProjectsByQueryAndTool(query, this.selectedTool);
    return [emptyProject, ...projects];
  };

  async onFromProjectSelected(project: ProjectListItem | null) {

    console.log('Project:', project);

    if (project && project.id !== -1) {
      this.selectedFromProject = await this.projectService.getProjectDetail(project.id);

      this.messageService.success('PROJECT.FOUND');
    } else {
      this.selectedFromProject = null;
    }

    this.toolSuggestionInput().disable();

    const amount = await this.getToolAmount();
    this.amountForm.controls.toolAmount.setValue(amount);

    this.amountForm.controls.toolAmount.enable();
    queueMicrotask(() => this.focusToAmountInput());

    // if (this.selectedTool?.toolProcessingType == "TOOL") {
    //   await this.onAmountSubmit();
    // } else {
    //   this.amountForm.controls.toolAmount.enable();
    //   queueMicrotask(() => this.focusToAmountInput());
    // }
  }

  public projectDisplayWithInventoryFn = (project: ProjectListItem): string => {
    return DisplayHelper.projectDisplayWithInventoryFn(project, this.inventoryLoc);
  };
}


